import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useEditorViewStore } from '@editorStores'
import { getPlans } from '@queries'
import { deletePlan } from '@mutations'
import { usePlanPdfViewer } from '../PlanPdfViewerContext'

/**
 * Deletes a saved plan, dropping it from the cached plan list, the "Pläne" highlight and its
 * unsaved PDF changes.
 */
const useDeletePlan = (plan: Plan) => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const selectedPlanGuid = useEditorViewStore(state => state.planView.selectedPlanGuid)
  const setSelectedPlanGuid = useEditorViewStore(state => state.setPlanViewSelectedPlanGuid)
  const { changedDocument, setChangedDocument } = usePlanPdfViewer()

  return useMutation(() => deletePlan.request(projectId as string, plan.guid), {
    onSuccess: () => {
      queryClient.setQueryData<Plan[]>(getPlans.getKey(projectId as string), plans =>
        plans?.filter(({ guid }) => guid !== plan.guid),
      )
      if (selectedPlanGuid === plan.guid) setSelectedPlanGuid(null)
      // Its unsaved PDF changes are gone with it.
      if (changedDocument?.guid === plan.guid) setChangedDocument(null)
      enqueueSnackbar('Plan erfolgreich gelöscht', { variant: 'success' })
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen des Plans'), {
        variant: 'error',
      })
    },
  })
}

export default useDeletePlan
